import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import DataTable from "src/components/dataTable";
import { compPlanStatementSelector } from "src/redux/selectors/custom-selector";
import { GridColumn as Column } from "@progress/kendo-react-grid";
import { columnMenuProps } from "src/components/customColumnMenu";
import { SortableHeaderCellTemplate } from "src/components/template/gridHeaderCellTemplate";
import { HeaderPage } from "../base/headerPage";
import { setCompPlanSummaryHeaderAction } from "src/redux/actions/compplan-actions";
import { getUserCredential } from "src/lib/cookies";
import { updateColumnReorderAsync } from "src/lib/common-service";
import { filterProviderSummaryColumnsForPdf, prepareReportSummaryHeaderColumnString } from "src/utils/utils";
import useCompPlanStatementService from "./service";

const CompPlanSummaryHeader = (props) => {
  const { isExporting } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const cookiesData = getUserCredential();
  const [gridColumns, setGridColumns] = useState([]);
  const [columnsData, setColumnsData] = useState([]);
  const [isColumnCheck, setIsColumnCheck] = useState(false);
  const [sortField, setSortField] = useState([]);
  const [isAPICalling, setAPICallingState] = useState(false);
  const { compPlanSummaryHeader } = useSelector(compPlanStatementSelector, shallowEqual);
  const reportService = useCompPlanStatementService();

  const getFieldTypeMap = (columns) => {
    const map = new Map();
    const walk = (cols) => {
      (cols || []).forEach((col) => {
        if (col?.childCols?.length) {
          walk(col.childCols);
          return;
        }
        if (col?.field) {
          map.set(col.field, col.type);
        }
      });
    };
    walk(columns);
    return map;
  };

  const getManageColumnsData = (columns) => {
    const flattenedColumns = [];

    columns.forEach((column) => {
      if (column.childCols?.length) {
        column.childCols.forEach((childColumn) => {
          flattenedColumns.push({ ...childColumn });
        });
        return;
      }

      flattenedColumns.push({ ...column });
    });

    return flattenedColumns;
  };

  const updateColumnsVisibility = (columns, columnsList) => {
    return columns.map((column) => {
      if (column.childCols?.length) {
        const updatedChildCols = column.childCols.map((childColumn) => {
          const dataItem = columnsList.find((item) => item.field === childColumn.field);
          return dataItem ? { ...childColumn, show: dataItem.show } : childColumn;
        });

        return {
          ...column,
          show: updatedChildCols.some((childColumn) => childColumn.show),
          childCols: updatedChildCols,
        };
      }

      const dataItem = columnsList.find((item) => item.field === column.field);
      return dataItem ? { ...column, show: dataItem.show } : column;
    });
  };

  const ensureOneVisibleColumn = (columns) => {
    const manageableColumns = getManageColumnsData(columns);
    const hasVisibleColumn = manageableColumns.some((column) => column.show && column.title !== "ACTION");

    if (hasVisibleColumn || manageableColumns.length === 0) {
      return columns;
    }

    const lastColumn = manageableColumns[manageableColumns.length - 1];

    return updateColumnsVisibility(
      columns,
      manageableColumns.map((column) => ({
        ...column,
        show: column.field === lastColumn.field,
      }))
    );
  };

  useEffect(() => {
    if (compPlanSummaryHeader.columns.length === 0) {
      setGridColumns([]);
      setColumnsData([]);
      return;
    }

    const updatedColumns = ensureOneVisibleColumn(compPlanSummaryHeader.columns);
    setGridColumns(updatedColumns);
    setColumnsData(getManageColumnsData(updatedColumns));

    if (updatedColumns !== compPlanSummaryHeader.columns) {
      dispatch(
        setCompPlanSummaryHeaderAction({
          columns: updatedColumns,
          data: compPlanSummaryHeader.data,
        })
      );
    }
  }, [compPlanSummaryHeader]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isColumnCheck) {
      updateColumnDataAsync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isColumnCheck]);

  const updateColumnDataAsync = async () => {
    const columnString = prepareReportSummaryHeaderColumnString(columnsData);
    if (!columnString) {
      setIsColumnCheck(false);
      return;
    }

    const reqData = {
      in_db_name: "procare_system",
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter_logical_cond: "Y",
      in_filter_label_cond: "N",
      in_column_info: columnString,
    };

    try {
      const res = await updateColumnReorderAsync(reqData, dispatch, history);
      if (res) {
        const newItems = ensureOneVisibleColumn(
          updateColumnsVisibility(compPlanSummaryHeader.columns, columnsData)
        );
        setGridColumns(newItems);
        dispatch(
          setCompPlanSummaryHeaderAction({
            columns: newItems,
            data: compPlanSummaryHeader.data,
          })
        );
      }
    } finally {
      setIsColumnCheck(false);
    }
  };

  const reorderColumnsByEvent = (reorderedColumns) => {
    const indexByField = new Map();
    reorderedColumns.forEach((column, idx) => {
      if (column.field) {
        indexByField.set(column.field, column.ariaColumnIndex ?? idx + 1);
      }
    });

    const getColumnPosition = (column) => {
      if (column.childCols?.length) {
        const childPositions = column.childCols
          .filter((childCol) => indexByField.has(childCol.field))
          .map((childCol) => indexByField.get(childCol.field));
        return childPositions.length ? Math.min(...childPositions) : Number.MAX_SAFE_INTEGER;
      }
      return indexByField.has(column.field) ? indexByField.get(column.field) : Number.MAX_SAFE_INTEGER;
    };

    const isReorderable = (column) =>
      !column.childCols?.length && column.show && column.title !== "" && indexByField.has(column.field);

    const reorderableSlots = [];
    compPlanSummaryHeader.columns.forEach((column, idx) => {
      if (isReorderable(column)) reorderableSlots.push(idx);
    });

    const sortedReorderable = compPlanSummaryHeader.columns
      .filter(isReorderable)
      .sort((a, b) => getColumnPosition(a) - getColumnPosition(b));

    const reorderedTopColumns = [...compPlanSummaryHeader.columns];
    reorderableSlots.forEach((slot, position) => {
      reorderedTopColumns[slot] = sortedReorderable[position];
    });

    return reorderedTopColumns;
  };

  const columnReorderData = async (reorderedColumns) => {
    const reorderedTopColumns = reorderColumnsByEvent(reorderedColumns);
    const flattenedColumns = getManageColumnsData(reorderedTopColumns);
    const columnString = prepareReportSummaryHeaderColumnString(flattenedColumns);
    if (!columnString) return;

    const reqData = {
      in_db_name: "procare_system",
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter_logical_cond: "Y",
      in_filter_label_cond: "N",
      in_column_info: columnString,
    };

    const res = await updateColumnReorderAsync(reqData, dispatch, history);
    if (res) {
      setGridColumns([]);
      setGridColumns(reorderedTopColumns);
      setColumnsData(getManageColumnsData(reorderedTopColumns));
      dispatch(
        setCompPlanSummaryHeaderAction({
          columns: reorderedTopColumns,
          data: compPlanSummaryHeader.data,
        })
      );
    }
  };

  const handleColumnMenu = async (columnMenuData, isFilter, isSort) => {
    if (!isSort) {
      return;
    }

    setSortField(columnMenuData);

    const sortItem = columnMenuData && columnMenuData.length ? columnMenuData[0] : null;
    setAPICallingState(true);
    try {
      await reportService.fetchSummaryHeaderProvider({
        in_sort_column: sortItem?.field || null,
        in_sort_order: sortItem?.dir ? sortItem.dir.toUpperCase() : "ASC",
      });
    } finally {
      setAPICallingState(false);
    }
  };

  const onClearFilter = async () => {
    setSortField([]);
    setAPICallingState(true);
    try {
      await reportService.fetchSummaryHeaderProvider();
    } finally {
      setAPICallingState(false);
    }
  };

  const GroupHeaderCell = (headerProps) => {
    return (
      <span className="k-cell-inner">
        <span className="k-link col-disabled-pointer-center">
          <span data-field={headerProps.field} className="k-column-title-export-header">
            {headerProps.title}
          </span>
        </span>
      </span>
    );
  };

  const ExportHeaderCell = (headerProps) => {
    return (
      <span className="k-cell-inner">
        <span className="k-link col-disabled-pointer-start">
          <span data-field={headerProps.field} className="k-column-title-export-header">
            {headerProps.title}
          </span>
        </span>
      </span>
    );
  };

  const renderColumn = (col, idx, parentKey = "root") => {
    if (!col.show || col.title === "") return false;

    const visibleChildCols = col.childCols?.filter((childCol) => childCol.show && childCol.title !== "") || [];
    const isLeaf = !col.childCols?.length;
    const isUtilizationGroup = col.title?.toLowerCase() === "utilization";
    const { columnMenu, headerCell: _headerCell, ...colMenuProps } = columnMenuProps(col);

    const headerCell = isExporting
      ? ExportHeaderCell
      : isLeaf
        ? SortableHeaderCellTemplate
        : isUtilizationGroup
          ? GroupHeaderCell
          : ExportHeaderCell;

    return (
      <Column
        {...colMenuProps}
        headerCell={headerCell}
        sortable={isLeaf}
        id={col.id || col.field || `${parentKey}_${col.title || idx}`}
        key={`${parentKey}_${col.field || col.title || idx}`}
        reorderable={isLeaf && col.reorderable !== false}
        children={
          visibleChildCols.length
            ? visibleChildCols.map((childCol, childIdx) => renderColumn(childCol, childIdx, col.title))
            : undefined
        }
        resizable={true}
        filterable={false}
        width={130}
      />
    );
  };

  const useCol = (columns) => {
    return columns.map((col, idx) => renderColumn(col, idx));
  };

  const cellRender = (tdElement, cellProps) => {
    if (tdElement && cellProps.rowType === "data") {
      const field = cellProps.field;
      const rawVal = field ? cellProps.dataItem?.[field] : undefined;
      const fieldTypeMap = getFieldTypeMap(compPlanSummaryHeader.columns);
      const fieldType = field ? fieldTypeMap.get(field) : undefined;

      const displayVal =
        fieldType === "boolean" ? (Number(rawVal) ? "true" : "false") : rawVal;

      const dataVal = <span title={displayVal}>{displayVal}</span>;
      return React.cloneElement(tdElement, tdElement.props, dataVal);
    }
    return tdElement;
  };

  const headerConfig = {
    title: "Provider Summary",
    id: "comp_plan_summary_header_provider",
    manage_col: {
      columnsData,
      setColumnsData: (columnsList) => {
        setColumnsData(columnsList);
        setIsColumnCheck(true);
      },
      setColumnCheck: setIsColumnCheck,
    },
  };

  const displayColumns = isExporting ? filterProviderSummaryColumnsForPdf(gridColumns) : gridColumns;
  const GroupColumn = useCol(displayColumns).filter(Boolean);

  return (
    <div>
      {!isExporting && (
        <div className="content-head">
          <HeaderPage pageProps={props} headerConfig={headerConfig} />
        </div>
      )}
      {GroupColumn.length > 0 && (
        <DataTable
          gridHeight=""
          initialLoading={true}
          isAPICalling={isAPICalling}
          cellRender={cellRender}
          columns={compPlanSummaryHeader.columns}
          data={compPlanSummaryHeader.data}
          customColumn={GroupColumn}
          id="comp-plan-summary-grid"
          isPagination={false}
          isResizable={true}
          columnReorderData={columnReorderData}
          handleColumnMenu={handleColumnMenu}
          onClearFilter={onClearFilter}
          initialSort={sortField}
          scrollable={isExporting ? "scrollable" : "scrollable"}
        />
      )}
    </div>
  );
};

export default React.memo(CompPlanSummaryHeader);
